import { cn } from '@utils'
import { InputType, StepButtonType } from '@constants'
import { useSessionStore } from '@stores'
import { getCustomInputClass, parseStyleProp } from '@utils/dom'
import { updateFontSizes } from '@utils/fontSizing'

interface ButtonStyles {
  style?: unknown
  style_type?: any
}

interface Input {
  id: string | number
  type: InputType
  formatted_label: string
  button_styles?: ButtonStyles
  disabled?: boolean
  classes?: string[]
}

interface InputButtonProps {
  input: Input
  onClick?: () => void
  disabled?: boolean
  className?: string
}

const InputButton = ({ input, onClick, disabled = false, className }: InputButtonProps) => {
  const session = useSessionStore(state => state.session)
  const isLegacy = session?.is_legacy || false

  const isLink =
    input.type === InputType.Link ||
    (input.button_styles?.style_type != null && input.button_styles.style_type === StepButtonType.TextOnly)

  const customClass = session ? getCustomInputClass(session as any, input as any, !isLink) : ''

  const handleClick = () => {
    if (disabled || input.disabled) {
      return
    }
    onClick?.()
  }

  const wrapperClass = !isLegacy ? 'flex justify-center w-full' : undefined
  const isDisabled = disabled || !!input.disabled

  // Campaign-controlled button widths (style_variables): links use reject_button_width,
  // buttons use accept_button_width — only when not legacy.
  const widthClass = !isLegacy ? (isLink ? 'reject_button_width' : 'accept_button_width') : undefined

  const body = (
    <div className={wrapperClass}>
      <div
        className={cn('w-full text-center', widthClass)}
        dangerouslySetInnerHTML={{ __html: updateFontSizes(input.formatted_label) }}
      />
    </div>
  )

  if (isLink) {
    return (
      <a
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        className={cn('input-link', customClass, className, isDisabled && 'pointer-events-none opacity-60')}
        style={parseStyleProp(input.button_styles?.style)}
        onClick={e => {
          e.preventDefault()
          handleClick()
        }}
        onKeyDown={e => {
          if (isDisabled) {
            return
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
          }
        }}>
        {body}
      </a>
    )
  }

  return (
    <button
      type="button"
      className={cn('btn-base input-choice-btn', customClass, className)}
      style={parseStyleProp(input.button_styles?.style)}
      onClick={handleClick}
      disabled={isDisabled}>
      {body}
    </button>
  )
}

export default InputButton
